import React, { useState, useEffect } from 'react';
import 'bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Table, Card, CardBody, Row, Col } from 'reactstrap';
import ReactPaginate from 'react-paginate';
import { FaClock, FaTimes, FaExclamationTriangle, FaSync, FaTrash, FaInfoCircle, FaBullseye, FaSlidersH } from 'react-icons/fa';
import showConfirmationAlert from '../../custom/confirmationAlert';
import { showErrorAlert, showLoadingAlert, showSuccessAlertWithRedirect } from '../../custom/sweetalertHelper';
import { convertTargetAudience } from '../../../slices/veloxity/audience/ConvertTargetAudience';
import DnaLookalikeModal from './DnaLookalikeModal';

function AudienceTable({ data, onDetailClick, onReCalculetedClick, onSelectionChange, onShowAlert, deleteAudienceClick, fetchAudienceConfig }) {
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'updateDate', direction: 'descending' });
    const [sortedData, setSortedData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const dispatch = useDispatch();
    const navigate = useNavigate();

    //Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 10;
    const pageCount = Math.ceil(sortedData.length / itemsPerPage);
    const currentItems = sortedData.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

    const [isLookalikeModalOpen, setIsLookalikeModalOpen] = useState(false);
    const [selectedLookalikeItem, setSelectedLookalikeItem] = useState(null);


    const toggleLookalikeModal = (item) => {
        setSelectedLookalikeItem(item); // Store the selected item
        setIsLookalikeModalOpen(!isLookalikeModalOpen);
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
    };


    const handleSortChange = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };


    useEffect(() => {
        const filteredData = data.filter(item =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

        const sortArray = type => {
            const types = {
                name: 'name',
                audienceCode: 'audienceCode',
                audienceSize: 'audienceSize',
                adIdCount: 'adIdCount',
                activity: 'activity',
                productPercentage: 'productPercentage',
                revenue: 'revenue',
                updateDate: 'updateDate',
                tag: 'tag'
            };
            const sortProperty = types[type];
            const sorted = [...filteredData].sort((a, b) => {
                if (a[sortProperty] < b[sortProperty]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortProperty] > b[sortProperty]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
            setSortedData(sorted);

            if (searchQuery || sortConfig.key) {
                setCurrentPage(0);
            }
        };

        sortArray(sortConfig.key);
    }, [data, sortConfig, searchQuery]);

    // Sayfa numarasını kontrol et ve gerekirse güncelle
    useEffect(() => {
        const pageCount = Math.ceil(sortedData.length / itemsPerPage);
        if (currentPage >= pageCount) {
            setCurrentPage(Math.max(pageCount - 1, 0));
        }
    }, [currentPage, sortedData, itemsPerPage]);


    const formatNumber = (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const handleCheckboxChange = (id) => {
        const isAlreadySelected = selectedIds.includes(id);
        if (!isAlreadySelected && selectedIds.length >= 3) {
            onShowAlert("You can select a maximum of 3 audiences.");
        } else {
            const newSelectedIds = isAlreadySelected
                ? selectedIds.filter(selectedId => selectedId !== id)
                : [...selectedIds, id];
            setSelectedIds(newSelectedIds);
            onSelectionChange(newSelectedIds);
        }
    };


    const getActivityColor = (activity) => {
        switch (activity) {
            case 'ACTIVE':
                return 'green';
            case 'INACTIVE':
                return 'coral';
            case 'MIXED':
                return 'blue';
            default:
                return 'black';
        }
    };

    const getTagBadge = (tag) => {
        const badges = {
            "USER_SELECTED": <span className="badge bg-primary-subtle text-primary badge-border">User Selected</span>,
            "DEFAULT": <span className="badge bg-secondary-subtle text-secondary badge-border">Default</span>,
            "AIMEE": <span className="badge bg-success-subtle text-success badge-border">AIMEE</span>,
            "DNA": <span className="badge bg-info-subtle text-info badge-border">Lookalike</span>
        };
        return badges[tag] || <span className="badge bg-dark-subtle text-body badge-border">Unknown</span>;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const day = date.getDate();
        const monthIndex = date.getMonth();
        const year = date.getFullYear();

        return `${day} ${months[monthIndex]}, ${year}`;
    };

    const renderStatus = (status, updateDate) => {
        if (status === 'SUCCEEDED') {
            return formatDate(updateDate);
        } else {
            return renderStatusIcon(status);
        }
    };

    const renderReportStatus = (item) => {
        if (item.report === null) {
            return (
                <div class="text-dark">
                    <i class="ri-information-line fs-17 align-middle"></i>  Not Created
                </div>
            );
        } else if (item.report.status === 'CALCULATING' || item.report.status === 'SCHEDULED') {
            return (
                <div class="text-secondary">
                    <i class="ri-refresh-line fs-17 align-middle"></i> Calculating
                </div>
            );
        } else if (item.report.status === 'SUCCEEDED') {
            return (
                <div class="text-success">
                    <i class="ri-checkbox-circle-line fs-17 align-middle"></i> Success
                </div>
            );
        } else {
            return (
                <div className="text-danger">
                    <i className="ri-close-circle-line fs-17 align-middle"></i> Failed
                </div>
            );
        }
    }

    const renderDataFile = (item) => {
        if (item.csv === null) {
            return (
                <div className="text-dark">
                    <i className="ri-information-line fs-17 align-middle"></i> Not Created
                </div>
            );
        } else if (item.csv.status === 'CALCULATING' || item.csv.status === 'SCHEDULED') {
            return (
                <div className="text-secondary">
                    <i className="ri-refresh-line fs-17 align-middle"></i> Calculating
                </div>
            );
        } else if (item.csv.status === 'SUCCEEDED') {
            return (
                <div className="text-success">
                    <i className="ri-checkbox-circle-line fs-17 align-middle"></i> Success
                </div>
            );
        } else {
            return (
                <div className="text-danger">
                    <i className="ri-close-circle-line fs-17 align-middle"></i> Failed
                </div>
            );
        }
    }

    const renderStatusIcon = (status) => {
        switch (status) {
            case 'FAILED':
                return <FaTimes color="red" />;
            case 'CALCULATING':
            case 'SCHEDULED':
                return <FaClock />
            default:
                return null;
        }
    };

    const handleAudienceNameClick = (item) => {
        switch (item.status) {
            case 'SUCCEEDED':
                onDetailClick(item);
                break;
            case 'CALCULATING':
            case 'SCHEDULED':
                showLoadingAlert('Please wait...', 'Data processing is in progress ! Please try again later.');
                break;
            case 'FAILED':
                showErrorAlert('Error!', 'Data Processing has not been completed successfully. Try calculating again.');
                break;
            default:
                onShowAlert("Current status does not allow recalculating. Please try again later.");
                break;
        }
    };
    const handleRecalculateClick = (item) => {
        switch (item.status) {
            case 'SUCCEEDED':
                showConfirmationAlert("Are you sure you want to recalculate the data set ?", () => onReCalculetedClick(item.audienceId));
                break;
            case 'CALCULATING':
            case 'SCHEDULED':
                showLoadingAlert('Please wait...', 'Data processing is in progress ! Please try again later.');
                break;
            case 'FAILED':
                showConfirmationAlert("Are you sure you want to recalculate the data set ?", () => onReCalculetedClick(item.audienceId));
                break;
            default:
                onShowAlert("Current status does not allow recalculating. Please try again later.");
                break;
        }
    };

    const handleSendToTargetAudience = (item) => {
        showConfirmationAlert(`Are you sure you want to send ${item.name} to the target audience?`, async () => {
            try {
                showLoadingAlert('Please wait...', 'Processing your request.');
                const response = await dispatch(convertTargetAudience(item.audienceId)).unwrap();
                if (response.success) {
                    // Navigate fonksiyonunu burada geçiyoruz
                    showSuccessAlertWithRedirect('Success!', response.message, '/target-audience', 'Go to Target Audience', navigate);
                } else {
                    showErrorAlert('Error!', response.message);
                }
            } catch (error) {
                showErrorAlert('Error!', error.message);
            }
        });
    };

    const NoDataOrErrorContent = () => (
        <tr>
            <td colSpan="9" style={{ textAlign: 'center' }}>
                <FaExclamationTriangle color="orange" /> <span>No data available</span>
            </td>
        </tr>
    );

    return (
        <Card>
            <CardBody>
                <Row className="g-4 mb-3">
                    <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                            <div className="search-box ms-2">
                                <input
                                    type="text"
                                    className="form-control search"
                                    placeholder="Search Audience Name..."
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                                <i className="ri-search-line search-icon"></i>
                            </div>
                        </div>

                    </Col>
                </Row>
                <Table hover responsive striped>
                    <thead className="table-light">
                        <tr style={{ textAlign: 'center' }}>
                            {/* <th>Ads Photo</th> */}
                            <th>
                                {  /* 
                             <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        value=""
                                        id="selectAllCheckboxes"
                                        onChange={(e) => {
                                            setSelectedAudiences(e.target.checked ? data.map(item => item.audienceId) : []);
                                        }}
                                    />
                                </div>
                                    */}
                            </th>
                            <th className="sort" onClick={() => handleSortChange('audienceCode')}>
                                Audience ID
                            </th>

                            <th className="sort" onClick={() => handleSortChange('name')}>
                                Audience Name
                            </th>
                            <th className="sort" onClick={() => handleSortChange('audienceSize')}>
                                Audience Size
                            </th>
                            <th className="sort" onClick={() => handleSortChange('adIdCount')}>
                                Ad ID Count
                            </th>
                            <th className="sort" onClick={() => handleSortChange('activity')}>
                                Activity
                            </th>
                            {/*
                             <th className="sort" onClick={() => handleSortChange('productPercentage')}>
                                Product & Percentage
                            </th>
                             *
                   
                            <th className="sort" onClick={() => handleSortChange('revenue')}>
                                Revenue
                             </th>
                               */}

                            <th className="sort" onClick={() => handleSortChange('updateDate')}>
                                Last Calculation Date
                            </th>
                            <th>AI Report</th>
                            <th>Data File</th>
                            <th className="sort" onClick={() => handleSortChange('tag')}>
                                Tags
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems && currentItems.length > 0 ? (
                            currentItems.map((item, index) => (
                                <tr key={index} style={{ textAlign: 'center', verticalAlign: 'middle', height: '70px' }}>
                                    {/* <td><img src={item.adsPhoto} alt="Ads" style={{ width: '50px', height: '50px' }} /></td> */}
                                    <td>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                value={item.audienceId}
                                                id={`checkbox${index}`}
                                                checked={selectedIds.includes(item.audienceId)}
                                                onChange={() => handleCheckboxChange(item.audienceId)}
                                            />
                                        </div>
                                    </td>
                                    <td>{item.audienceCode}</td>
                                    <td style={{ backgroundColor: 'transparent', color: '#295B82', textDecoration: 'underline', border: 'none', padding: 0 }} onClick={() => handleAudienceNameClick(item)}>
                                        {item.name}
                                    </td>
                                    <td style={{ fontWeight: 'bold' }}>{formatNumber(item.audienceSize)}</td>
                                    <td style={{ fontWeight: 'bold' }}>{formatNumber(item.adIdCount)}</td>
                                    <td> <div style={{ textAlign: 'center', color: getActivityColor(item.activity) }}> {item.activity} </div></td>
                                    {/**
                                    <td>{item.productPercentage} - <span style={{ fontWeight: 'bold' }}>0%</span> </td>
                                    <td style={{ fontWeight: 'bold', fontSize: '1.1em' }}>${formatNumber(item.revenue)}</td>
                                     */}

                                    <td>
                                        {renderStatus(item.status, item.updateDate)}
                                    </td>
                                    <td>
                                        {renderReportStatus(item)}
                                    </td>
                                    <td>
                                        {renderDataFile(item)}
                                    </td>
                                    <td>
                                        {getTagBadge(item.tag)}
                                    </td>

                                    <td>
                                        <div class="dropdown">
                                            <a href="#" role="button" id="dropdownMenuLink" data-bs-toggle="dropdown" aria-expanded="false">
                                                <i class="ri-more-2-fill"></i>
                                            </a>

                                            <ul className="dropdown-menu" aria-labelledby="dropdownMenuLink">
                                                <li>
                                                    <a className="dropdown-item" href="javascript:void(0);"
                                                        onClick={() => handleRecalculateClick(item)}
                                                        style={{ pointerEvents: item.operationStatus.reCalculateActive ? 'auto' : 'none', color: item.operationStatus.reCalculateActive ? 'blue' : 'gray' }}>
                                                        <FaSync style={{ marginRight: '5px' }} /> Recalculate
                                                    </a>
                                                </li>
                                                <li>
                                                    <a className="dropdown-item" href="javascript:void(0);"
                                                        onClick={() => deleteAudienceClick(item)}
                                                        style={{ pointerEvents: item.operationStatus.deleteActive ? 'auto' : 'none', color: item.operationStatus.deleteActive ? 'red' : 'gray' }}>
                                                        <FaTrash style={{ marginRight: '5px' }} /> Delete
                                                    </a>
                                                </li>
                                                <li>
                                                    {item.tag !== 'DNA' && (
                                                        <a className="dropdown-item" href="javascript:void(0);"
                                                            onClick={() => fetchAudienceConfig(item)}
                                                            style={{ pointerEvents: 'auto', color: 'green' }}>
                                                            <FaInfoCircle style={{ marginRight: '5px' }} /> Info
                                                        </a>
                                                    )}
                                                </li>
                                                <li>
                                                    <a className="dropdown-item" href="javascript:void(0);"
                                                        onClick={() => handleSendToTargetAudience(item)}
                                                        style={{ pointerEvents: item.operationStatus.moveToTargetActive ? 'auto' : 'none', color: item.operationStatus.moveToTargetActive ? 'purple' : 'gray' }}>
                                                        <FaBullseye style={{ marginRight: '5px' }} /> Send to Target Audience
                                                    </a>
                                                </li>
                                                <li>
                                                    {item.tag !== 'DNA' && (
                                                        <a className="dropdown-item" href="javascript:void(0);"
                                                            onClick={() => toggleLookalikeModal(item)}
                                                            style={{ pointerEvents: item.operationStatus.dnaLookalikeActive ? 'auto' : 'none', color: item.operationStatus.dnaLookalikeActive ? 'purple' : 'gray' }}>
                                                            <FaSlidersH style={{ marginRight: '5px' }} /> Dna Lookalike
                                                        </a>
                                                    )}
                                                </li>
                                            </ul>
                                        </div>
                                    </td>


                                </tr>
                            ))) : (<NoDataOrErrorContent />)
                        }
                    </tbody>

                </Table>

                {selectedLookalikeItem && (
                    <DnaLookalikeModal
                        isOpen={isLookalikeModalOpen}
                        toggle={toggleLookalikeModal}
                        audienceId={selectedLookalikeItem.audienceId}
                        audience= {"notTarget"}
                    />
                )}
                <ReactPaginate
                    previousLabel={'Prev'}
                    nextLabel={'Next'}
                    breakLabel={'...'}
                    pageCount={pageCount}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={handlePageClick}
                    containerClassName={'pagination justify-content-end'}
                    subContainerClassName={'pages pagination'}
                    activeClassName={'active'}
                    forcePage={currentPage}
                />
            </CardBody>
        </Card>
    );
}

export default AudienceTable;